import { CloseOutlined, InfoCircleOutlined, PlusOutlined } from '@ant-design/icons';
import { theme } from 'antd';
import { removeEmptyOrNull } from 'library/helpers/utility';
import fieldsValidator from 'library/helpers/validators/fieldsValidator';
import { useManagePermittedRoles } from 'library/hooks';
import { Fragment, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { categoriesActions } from 'reduxStore/categories/slice';
import { selectDefaultCategories } from 'reduxStore/defaultCategories/selectors';
import { selectCategoryTypes, selectFieldTypes } from 'reduxStore/types/selectors';
import {
	Button,
	Col,
	Flex,
	Form,
	Input,
	List,
	Modal,
	Radio,
	Row,
	Segmented,
	Select,
	Tooltip,
	Typography
} from 'zui/Antd';

const { Title, Text } = Typography;
const { TextArea } = Input;

const CreateCategoryModal = ({ children, visible: visibleProp, setVisible: setVisibleProp }) => {
	const canAddCategory = useManagePermittedRoles('addCategory');
	const permissionDeniedMessage = "You don't have permission to perform this action.";
	const {
		token: {
			paddingXL,
			paddingLG,
			fontSizeSM,
			colorTextDescription,
			fontWeightStrong,
			colorIcon,
			paddingXS,
			paddingSM,
			marginMD,
			fontSize,
			colorBgBase,
			boxShadowSecondary,
			colorBgElevated,
			colorText,
			colorTextLabel,
			paddingXXS,
			colorSplit
		}
	} = theme.useToken();

	const dispatch = useDispatch();
	const { loadingCreateCategory } = useSelector((state) => state.Categories);
	const categoryTemplates = useSelector(selectDefaultCategories) || [];
	const fieldTypes = useSelector(selectFieldTypes);
	const categoryTypes = useSelector(selectCategoryTypes);

	const [form] = Form.useForm();
	const [internalVisible, setInternalVisible] = useState(false);
	const [activeTab, setActiveTab] = useState('template');
	const selectedTypeID = Form.useWatch('typeID', form);

	const visible = visibleProp ?? internalVisible;
	const setVisible = setVisibleProp ?? setInternalVisible;

	// 🧹 Reset Template cuando cambia typeID
	useEffect(() => {
		if (selectedTypeID) {
			form.setFieldsValue({ selectedCardTemplate: undefined });
		}
	}, [selectedTypeID, form]);

	const handleCancel = () => {
		form.resetFields();
		setVisible(false);
	};

	const handleSubmit = (values) => {
		const { name, description, typeID, fields = [], selectedCardTemplate } = values;

		// ✅ Common object for all categories (booleans and typeID never null)
		const commonCategoryData = {
			typeID,
			isDefault: false,
			isBookable: typeID === 3,
			isUnderCleaning: false
		};

		if (activeTab === 'template') {
			// 🚨 Validation: if using a template, custom fields are not allowed
			if (fields.length > 0) {
				form.setFields([
					{
						name: 'fields',
						errors: ['You cannot add custom fields when creating a category from a template.']
					}
				]);
				return;
			}

			if (!selectedCardTemplate) {
				form.setFields([
					{
						name: 'selectedCardTemplate',
						errors: ['Please select a template.']
					}
				]);
				return;
			}

			// 🚀 Prepare the payload
			const payload = {
				...removeEmptyOrNull({ name, description }), // clean only name/description
				...commonCategoryData,
				defaultCategoryID: selectedCardTemplate,
				createCategoryFromTemplate: true
			};

			// Dispatch
			dispatch(
				categoriesActions.createCategory({
					data: payload
				})
			);
		} else {
			// 🚀 Prepare the payload
			const payload = {
				...removeEmptyOrNull({ name, description }),
				...commonCategoryData
			};

			// Dispatch
			dispatch(
				categoriesActions.createCategory({
					data: payload,
					fields
				})
			);
		}

		// 🔥 Close modal and reset form
		setTimeout(() => {
			setVisible(false);
			form.resetFields();
		}, 500);
	};

	const filteredCardTemplates = categoryTemplates?.filter((template) => template.typeID === selectedTypeID);

	const cardTemplateOptions = filteredCardTemplates?.map((template) => ({
		value: template.categoryID,
		label: (
			<Flex align='center' gap={8}>
				<Text>{template.name}</Text>
				<Tooltip
					placement='right'
					color={colorBgBase}
					styles={{
						body: {
							padding: paddingXS,
							boxShadow: boxShadowSecondary,
							width: 200,
							maxHeight: 560,
							overflowY: 'auto'
						}
					}}
					title={
						<Flex vertical gap={marginMD}>
							<Title level={5} style={{ fontWeight: fontWeightStrong, margin: 0 }}>
								Predefined Fields
							</Title>
							<List
								dataSource={template.fields}
								size='small'
								renderItem={(field) => {
									const fieldTypeName =
										fieldTypes.find((type) => type.fieldTypeID === field.fieldTypeID)?.name ||
										'Unknown';

									return (
										<List.Item style={{ padding: 0, border: 0 }}>
											<Flex vertical style={{ marginBottom: marginMD }}>
												<Text>{field.title}</Text>
												<Text type='secondary'>{fieldTypeName}</Text>
											</Flex>
										</List.Item>
									);
								}}
							/>
						</Flex>
					}
				>
					<InfoCircleOutlined style={{ color: colorIcon, fontSize: fontSizeSM }} />
				</Tooltip>
			</Flex>
		)
	}));

	const handleTabChange = (value) => {
		setActiveTab(value);
	};

	return (
		<Fragment>
			<Tooltip title={!canAddCategory ? permissionDeniedMessage : 'Create New Category'}>
				<span>
					{children ? (
						children
					) : (
						<Button
							type='primary'
							disabled={!canAddCategory}
							onClick={() => canAddCategory && setVisible(true)}
						>
							Create New Category
						</Button>
					)}
				</span>
			</Tooltip>

			<Modal
				open={visible}
				onCancel={handleCancel}
				destroyOnClose
				width={1400}
				closeIcon={<CloseOutlined />}
				centered
				footer={[
					<Button key='cancel' disabled={loadingCreateCategory} onClick={handleCancel}>
						Cancel
					</Button>,
					<Button
						htmlType='submit'
						type='primary'
						disabled={loadingCreateCategory}
						loading={loadingCreateCategory}
						key='submit'
						form='createCategoryForm'
					>
						Create Category
					</Button>
				]}
				styles={{
					body: {
						height: 764,
						display: 'flex',
						flexDirection: 'column'
					},
					content: { padding: paddingXL, height: '100%' },
					footer: { marginTop: 0 }
				}}
			>
				<Form
					form={form}
					name='createCategoryForm'
					onFinish={handleSubmit}
					layout='vertical'
					disabled={loadingCreateCategory}
					style={{ height: '100%', display: 'flex', flexDirection: 'column', flex: 1 }}
				>
					<Flex gap={paddingXL} justify='space-between' align='stretch' style={{ height: '100%', flex: 1 }}>
						{/* LEFT COLUMN */}
						<Flex vertical gap={paddingLG} flex={1} style={{ height: '100%' }}>
							<Flex vertical style={{ marginBottom: paddingXS }}>
								<Title level={4} style={{ margin: 0 }}>
									Create New Category
								</Title>
								<Text
									style={{
										fontSize: fontSizeSM,
										color: colorTextDescription,
										lineHeight: '20px'
									}}
								>
									A category defines a group of devices in your workspace.
									<br /> You can create a completely new category with custom input fields, or
									duplicate one of Zezamii’s template categories, which come with predefined fields.
								</Text>
							</Flex>

							<Form.Item
								name='name'
								label='Category Name'
								style={{ fontWeight: fontWeightStrong, margin: 0 }}
								rules={fieldsValidator.Name}
								required={false}
							>
								<Input placeholder='Enter category name' style={{ marginTop: 4 }} />
							</Form.Item>

							<Form.Item
								name='typeID'
								label='Type'
								style={{ fontWeight: fontWeightStrong, margin: 0 }}
								rules={[{ required: true, message: 'Type is required' }]}
								required={false}
							>
								<Radio.Group style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
									{categoryTypes.map((type) => (
										<Radio
											key={type.typeID}
											value={type.typeID}
											style={{ color: colorTextLabel, fontWeight: '500', lineHeight: '22px' }}
										>
											{type.name}
											<Tooltip
												color={colorBgElevated}
												style={{ color: colorText }}
												title={
													<span style={{ color: colorText }}>
														{type.description ||
															(type.name === 'Asset'
																? 'Any non-digital item in your workspace—such as fire hydrants, furniture, or signage—that you want to track or manage.'
																: type.name === 'Device'
																	? 'A digital item in your workspace that can be connected, controlled, or monitored—such as locks, readers, or sensors.'
																	: 'Any item or space in your workspace that can be reserved—like meeting rooms, desks, or shared equipment.')}
													</span>
												}
											>
												<InfoCircleOutlined
													style={{
														marginLeft: paddingXXS,
														color: colorIcon,
														fontSize: fontSizeSM
													}}
												/>
											</Tooltip>
										</Radio>
									))}
								</Radio.Group>
							</Form.Item>
						</Flex>

						{/* Vertical Divider */}
						<div
							style={{
								width: 1,
								backgroundColor: colorSplit,
								margin: `0 ${paddingXS}px 0px`
							}}
						/>

						{/* RIGHT COLUMN */}
						<Flex vertical gap={paddingXS} flex={1}>
							<Flex>
								<Segmented
									value={activeTab}
									onChange={handleTabChange}
									options={[
										{ label: 'Template', value: 'template' },
										{ label: 'Custom', value: 'custom' }
									]}
									style={{
										padding: 0,
										marginBottom: paddingLG
									}}
								/>
							</Flex>

							{/* 🔁 Conditional content */}
							{activeTab === 'custom' && (
								<Flex vertical gap={paddingLG}>
									<Form.Item
										name='description'
										label='Description'
										style={{
											fontWeight: fontWeightStrong,
											margin: 0
										}}
									>
										<TextArea
											style={{ color: colorTextDescription }}
											placeholder='Enter category description'
											rows={4}
										/>
									</Form.Item>

									<Flex vertical gap={paddingSM}>
										<Text style={{ fontWeight: fontWeightStrong, marginTop: paddingXS }}>
											Category Custom Fields
										</Text>

										<Form.List name='fields'>
											{(fields, { add, remove }) => (
												<Flex
													vertical
													style={{
														flex: 1,
														maxHeight: 460,
														overflowY: 'auto',
														overflowX: 'hidden',
														scrollbarWidth: 'thin',
														padding: 4
													}}
												>
													{fields.map(({ key, name, ...restField }) => (
														<Row
															key={key}
															gutter={10}
															style={{ marginBottom: marginMD }}
															align='middle'
														>
															<Col span={14}>
																<Form.Item
																	{...restField}
																	name={[name, 'title']}
																	rules={[
																		{
																			required: true,
																			message: 'Please enter field name'
																		}
																	]}
																	style={{ marginBottom: 0 }}
																>
																	<Input placeholder='Field Name' />
																</Form.Item>
															</Col>

															<Col span={8}>
																<Form.Item
																	{...restField}
																	name={[name, 'fieldTypeID']}
																	rules={[
																		{
																			required: true,
																			message: 'Please select field type'
																		}
																	]}
																	style={{ marginBottom: 0 }}
																>
																	<Select
																		placeholder='Type'
																		options={fieldTypes.map((type) => ({
																			label: type.name,
																			value: type.fieldTypeID
																		}))}
																	/>
																</Form.Item>
															</Col>

															<Col span={2}>
																<CloseOutlined
																	onClick={() => remove(name)}
																	style={{
																		fontSize: fontSize,
																		color: colorTextDescription
																	}}
																/>
															</Col>
														</Row>
													))}

													<Form.Item>
														<Button
															type='dashed'
															onClick={() => {
																add({ title: '', fieldTypeID: 1 });
															}}
															block
															icon={<PlusOutlined />}
														>
															Add Custom Field
														</Button>
													</Form.Item>
												</Flex>
											)}
										</Form.List>
									</Flex>
								</Flex>
							)}

							{activeTab === 'template' && (
								<>
									{!selectedTypeID ? (
										<Text
											type='secondary'
											style={{
												fontSize: fontSizeSM,
												color: colorTextDescription
											}}
										>
											Please select a Type first to see available templates.
										</Text>
									) : (
										<Form.Item
											name='selectedCardTemplate'
											label='Category Template'
											rules={[{ required: true, message: 'Please select a card template' }]}
											style={{ fontWeight: fontWeightStrong, width: '100%' }}
											required={false}
										>
											<Select
												options={cardTemplateOptions}
												placeholder='Select Category Template'
												style={{ fontWeight: 400 }}
												showSearch={false}
												allowClear
											/>
										</Form.Item>
									)}
								</>
							)}
						</Flex>
					</Flex>
				</Form>
			</Modal>
		</Fragment>
	);
};

export default CreateCategoryModal;
